import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {copilotSpaceIdFromThread} from '../utils/copilot-space-id-from-thread'
import {useFetchCopilotSpaces} from '.'
import type {IndexCopilotSpace} from '@github-ui/copilot-chat/utils/copilot-chat-types'
/**
 * @returns The most recent copilot spaces. If no threads have copilot spaces,
 * returns the five most recently updated spaces. If some threads are attached
 * to spaces, returns the spaces for those threads first, then fills in the rest
 * of the results with the most recently updated spaces without duplicates.
 */
export function useRecentCopilotSpaces() {
  const {threads} = useChatState()

  // Sort threads by updatedAt date (most recent first) and extract copilot space IDs
  const threadSpaceIds = Array.from(threads.values())
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .map(thread => copilotSpaceIdFromThread(thread))
    .filter(t => t !== null)

  const queryResult = useFetchCopilotSpaces(true)
  const {data: allSpaces} = queryResult
  if (allSpaces === undefined) return queryResult

  // Get spaces that correspond to threads, maintaining thread sort order and deduplicating
  const seenSpaces = new Set<string>()
  const threadBasedSpaces = threadSpaceIds
    .map(id => allSpaces.find(space => id.id === space.id && id.owner === space.owner))
    .filter((space): space is IndexCopilotSpace => {
      if (space === undefined) return false
      const spaceKey = `${space.owner}:${space.id}`
      if (seenSpaces.has(spaceKey)) return false
      seenSpaces.add(spaceKey)
      return true
    })

  // Sort all spaces by updatedAt (most recent first) to fill remaining slots
  const spacesSortedByUpdatedAt = [...allSpaces].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  )

  // Fill remaining slots with most recent spaces that aren't already included
  const remainingSpaces = spacesSortedByUpdatedAt.filter(space => {
    const spaceKey = `${space.owner}:${space.id}`
    return !seenSpaces.has(spaceKey)
  })

  // Combine thread-based spaces with remaining most recent spaces, limit to 5 total
  const finalSpaces: IndexCopilotSpace[] = [...threadBasedSpaces, ...remainingSpaces].slice(0, 5)

  return {
    ...queryResult,
    data: finalSpaces,
  }
}
